import { useEffect } from "react";
import { useFlags, useLDClient } from "launchdarkly-react-client-sdk";
import type { FeatureFlagConfig, NumberFeatureFlagConfig } from "../types";
import { isFlagDeprecated } from "../utils/deprecation";

const warnedFlags = new Set<string>();

/**
 * Test-only helper: clears the once-per-flag dedupe set so specs don't leak
 * warning state into each other. Not part of the module's public barrel.
 * @internal
 */
export function resetDeprecationWarnings(): void {
  warnedFlags.clear();
}

function warnIfDeprecated(config: FeatureFlagConfig): void {
  if (!import.meta.env.DEV) return;
  if (warnedFlags.has(config.flag)) return;
  if (!isFlagDeprecated(config)) return;
  warnedFlags.add(config.flag);
  console.warn(
    `[feature-flags] Flag "${config.flag}" is past its deprecation date (${config.deprecateAfter}) and should be removed. See src/feature-flags/AGENTS.md ("Deprecating / removing a flag").`,
  );
}

export function useFeatureFlagValue(config: FeatureFlagConfig): boolean {
  const flags = useFlags();
  useEffect(() => {
    warnIfDeprecated(config);
  }, [config]);
  const value = flags[config.flag];
  return typeof value === "boolean" ? value : config.default;
}

export function useAllFeatureFlags() {
  return useFlags();
}

/**
 * Read a numeric LaunchDarkly flag, falling back to `config.default` when LD
 * returns a non-finite or absent value. Callers clamp to their own valid range.
 */
export function useFeatureFlagNumber(config: NumberFeatureFlagConfig): number {
  const flags = useFlags();
  const value = flags[config.flag];
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : config.default;
}

export { useLDClient };
